<script setup>
import PriorityIcon from '@/components/activity/PriorityIcon.vue'
import { qualifiedIndex as qualifiedAt } from '@/lib/timelineQualified.js'
import RecordLink from '@/components/record/RecordLink.vue'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useDebounce } from '@/composables/useDebounce.js'
import { useI18n } from 'vue-i18n'
import { localList } from '@addictivegroup/addictive-offline'
import { getRelated } from '@/lib/records.js'
import { useMeta } from '@/stores/meta.js'
import { offline } from '@/lib/offline.js'
import { useUi } from '@/stores/ui.js'
import { ENTITIES } from '@/lib/entities.js'
import { formatDateTime, formatRelative } from '@/lib/format.js'
import { activityTypeIcon, statusLabel, STATUS_TONES, enumLabel, fieldLabel } from '@/lib/labels.js'
import UiAvatar from '@/components/ui/UiAvatar.vue'
import UiBadge from '@/components/ui/UiBadge.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiSpinner from '@/components/ui/UiSpinner.vue'
import UiEmpty from '@/components/ui/UiEmpty.vue'
import UiMenu from '@/components/ui/UiMenu.vue'
import NoteMoveDialog from './NoteMoveDialog.vue'
import NoteToActivityDialog from './NoteToActivityDialog.vue'
import { moveNote } from '@/lib/notes.js'
import { useSession } from '@/stores/session.js'
import NoteComposer from './NoteComposer.vue'
import ActivityActions from '@/components/activity/ActivityActions.vue'
import FollowUpBadge from '@/components/activity/FollowUpBadge.vue'
import ChangeList from './ChangeList.vue'
import RichText from './RichText.vue'
import ClampedRichText from './ClampedRichText.vue'
import NoteAttachments from './NoteAttachments.vue'
import { pendingUploads } from '@/lib/noteUploads.js'

/**
 * A record's timeline (Dynamics: Timeline): notes, activities, its own history and mentions
 * elsewhere, newest first. Offline it shows the notes and activities kept on the device.
 * Gathered from the records around it (D-226): an opportunity with its lead's story up to the
 * qualification, an account with its cases (as the buyer and as the end customer) and leads —
 * or only its own, or one case — a contact with the conversations it took part in. Each item
 * from elsewhere says where it comes from.
 */
// `noteTo`: where a note written here goes, when not to this record (an activity shown with its case's timeline).
const CREATE = [
  { type: 'call', icon: 'fa-phone', label: 'actions.addCall' },
  { type: 'meeting', icon: 'fa-people-group', label: 'actions.addMeeting' },
  { type: 'task', icon: 'fa-square-check', label: 'actions.addTask' },
  { type: 'email', icon: 'fa-envelope', label: 'actions.addEmail' },
]
const props = defineProps({
  entity: { type: String, required: true },
  record: { type: Object, required: true },
  noteTo: { type: Object, default: null },
  replyTo: { type: Object, default: null },
  // An item to open on (D-262: a summary's source): {uuid, at?, quote?} — the page is loaded from
  // its moment, the item scrolled to and flashed, the quote marked in its text.
  focus: { type: Object, default: null },
  // The AI summary's reach (D-264): the moment of the last item it covers; newer items above the line are not in it yet.
  coverage: { type: String, default: null },
})
// An item asked for that this timeline does not hold (on a record it does not gather): the page opens it there.
const emit = defineEmits(['focus-missing'])
const { t } = useI18n()
const ui = useUi()
const meta = useMeta()

const filter = ref('all')
const items = ref([])
const nextBefore = ref(null)
const loading = ref(false)
const source = ref('server')
const kinds = { all: '', notes: 'note', activities: 'activity', comments: 'field_comment', changes: 'history' }
/** What the timeline gathers: 'all' (with the records around), 'own', or 'case:<entity>:<uuid>' (an account's case). */
const scope = ref('all')
const around = ref(null)
const gathers = computed(() => ['opportunity', 'account', 'contact'].includes(props.entity))

/** A note shown here that is on something else: one of this record's activities. */
const onOther = (note) => !!note.noteable?.uuid && note.noteable.uuid !== props.record.uuid

async function localItems() {
  const [type, id] = [props.entity, props.record.uuid]
  const activities = await localList(offline, 'activity', { predicate: (a) => a.regarding?.uuid === id && (a.regarding_type || a.regarding?.entity) === type, size: 200 })
  // Its notes, and those of its activities (a call's summary), as Core gives them.
  const ofActivities = new Set(type === 'activity' ? [] : activities.data.map((a) => a.uuid))
  const notes = await localList(offline, 'note', {
    predicate: (n) => {
      const on = n.noteable_type || n.noteable?.entity
      return (n.noteable?.uuid === id && on === type) || (on === 'activity' && ofActivities.has(n.noteable?.uuid))
    },
    size: 200,
  })
  return [
    ...notes.data.map((n) => ({ kind: 'note', at: n.created_at, record: n })),
    ...activities.data.map((a) => ({ kind: 'activity', at: a.completed_at || a.start_at || a.due_at || a.created_at, record: a })),
  ].sort((a, b) => String(b.at).localeCompare(String(a.at)))
}

// A note moved elsewhere or made an activity (D-347): by its own action, never with an edit; one
// still waiting to be sent has none yet.
const session = useSession()
const moving = ref(null)
const converting = ref(null)
// A formatted text with any words in it (an empty description is a doc too).
function hasText(doc) {
  const walk = (n) => (n?.type === 'text' && n.text?.trim()) || n?.type === 'mention' || (n?.content || []).some(walk)
  return !!doc && walk(doc)
}
function noteActions(note) {
  if (note._pending || note.abilities?.move === false) return []
  return [
    { label: t(note.noteable ? 'notes.actions.move' : 'notes.actions.attach'), icon: 'fa-right-left', disabled: !offline.state.online, action: () => (moving.value = note) },
    ...(session.has('activity:create') ? [{ label: t('notes.actions.convert'), icon: 'fa-arrow-right-arrow-left', action: () => (converting.value = note) }] : []),
  ]
}
function moved(to) {
  const note = moving.value
  moving.value = null
  moveNote(note, to, { done: () => load() })
}

async function load(more = false) {
  loading.value = true
  try {
    // Opening on an item: the first page starts just after its moment, so it is near the top.
    const from = !more && props.focus?.at && !focused.value ? new Date(new Date(props.focus.at).getTime() + 1000).toISOString() : undefined
    const query = {
      limit: 30, kinds: kinds[filter.value] || undefined, before: more ? nextBefore.value : from,
      scope: scope.value === 'own' ? 'own' : undefined,
      case: scope.value.startsWith('case:') ? scope.value.slice(5) : undefined,
    }
    const result = await getRelated(props.entity, props.record.uuid, 'timeline', query, localItems)
    source.value = result.source
    const data = Array.isArray(result.data) ? result.data : []
    items.value = more ? [...items.value, ...data] : data
    nextBefore.value = result.body?.next_before ?? null
    if (!more) around.value = result.body?.around ?? null
    if (props.focus && !focused.value) await showFocus()
  } catch (e) {
    ui.error(e.message)
  } finally {
    loading.value = false
  }
}
/** The focused item once shown; a page without it loads older pages a few times before giving up. */
const root = ref(null)
const focused = ref(false)
let focusTries = 0
async function showFocus() {
  await nextTick()
  const el = root.value?.querySelector(`[data-item="${props.focus.uuid}"]`)
  if (!el) {
    if (nextBefore.value && focusTries++ < 5) await load(true)
    else emit('focus-missing', props.focus)
    return
  }
  focused.value = true
  el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  el.classList.add('timeline-focus')
  setTimeout(() => el.classList.remove('timeline-focus'), 4000)
  if (props.focus.quote) markQuote(el, props.focus.quote)
}
/** Wraps the quote's first occurrence in the element's text nodes in <mark>, whitespace and case aside. */
function markQuote(el, quote) {
  const flat = (t) => t.replace(/\s+/g, ' ').trim().toLowerCase()
  const wanted = flat(quote)
  if (!wanted) return
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.nodeValue || ''
    const at = flat(text).indexOf(wanted)
    if (at < 0) continue
    // Map the position back to the raw text (collapsed runs of whitespace count once).
    let i = 0, seen = 0, start = -1, end = -1
    for (; i < text.length; i++) {
      const ws = /\s/.test(text[i])
      if (ws && i > 0 && /\s/.test(text[i - 1])) continue
      if (seen === at && start < 0) start = i
      seen++
      if (seen === at + wanted.length) { end = i + 1; break }
    }
    if (start < 0) return
    const range = document.createRange()
    range.setStart(node, start)
    range.setEnd(node, end < 0 ? text.length : end)
    const mark = document.createElement('mark')
    mark.className = 'timeline-quote'
    range.surroundContents(mark)
    mark.scrollIntoView({ block: 'center', behavior: 'smooth' })
    return
  }
}
// Asked again (a quote clicked on this page): everything shown, the item scrolled to — loaded from its moment if not here.
watch(() => (props.focus ? [props.focus.uuid, props.focus.quote, props.focus.nonce].join('|') : ''), (key) => {
  if (!key) return
  focused.value = false
  focusTries = 0
  if (filter.value !== 'all' || scope.value !== 'all') {
    filter.value = 'all'
    scope.value = 'all'
    return
  }
  if (root.value?.querySelector(`[data-item="${props.focus.uuid}"]`)) showFocus()
  else load()
})
watch(() => props.record.uuid, () => { scope.value = 'all' })
watch(() => [props.record.uuid, filter.value, scope.value], () => load(), { immediate: true })
// A pull brings changes one by one, hundreds at a time: one reload for them all, not one each.
const reloadOnFeed = useDebounce(() => load(), 400)
const stop = offline.on((event, payload) => {
  if (event === 'change' && ['note', 'activity'].includes(payload?.entity)) reloadOnFeed()
})
onBeforeUnmount(() => stop())

/** Where an item from another record comes from: the case, its role, or a conversation taken part in. */
function originText(origin) {
  if (origin.kind === 'participant') return t('timeline.origin.participant')
  return `${t('timeline.origin.from')} ${origin.label}${origin.role && origin.role !== 'lead' ? ` (${t(`timeline.origin.roles.${origin.role}`)})` : ''}`
}
/** The qualification on an opportunity's timeline: above its lead's story, when shown. */
const qualifiedIndex = computed(() => (props.entity === 'opportunity' && scope.value === 'all' ? qualifiedAt(visible.value, around.value?.lead) : -1))
/** Where the AI summary's reach ends: the first (newest-first) item it covers, when newer ones stand above it. */
const coverageIndex = computed(() => {
  if (!props.coverage) return -1
  const i = visible.value.findIndex((item) => item.at && item.at <= props.coverage)
  return i > 0 ? i : -1
})
const caseOptions = computed(() => (around.value?.cases || []).map((c) => ({ value: `case:${c.entity}:${c.uuid}`, label: `${c.label} — ${t(`timeline.origin.roles.${c.role}`)}${c.status && c.status !== 'open' ? ` · ${statusLabel(c.entity, c.status)}` : ''}` })))

/**
 * The notes on an activity shown here, oldest first, by its uuid: they are read inside its block
 * (D-294) — what closing it said, a call's summary. Only with everything shown: under "Notes" no
 * activity stands here, and a note whose activity is not on the page loaded stands on its own.
 */
const notesOf = computed(() => {
  if (filter.value !== 'all') return {}
  const shown = new Set(items.value.filter((i) => i.kind === 'activity').map((i) => i.record?.uuid))
  const map = {}
  for (const i of items.value) {
    const n = i.record
    if (i.kind !== 'note' || (n?.noteable_type || n?.noteable?.entity) !== 'activity' || !shown.has(n.noteable?.uuid)) continue
    ;(map[n.noteable.uuid] ||= []).push(i)
  }
  for (const list of Object.values(map)) list.sort((a, b) => String(a.at).localeCompare(String(b.at)))
  return map
})
const inBlock = (i) => i.kind === 'note' && !!notesOf.value[i.record?.noteable?.uuid]

const visible = computed(() => items.value.filter((i) => !inBlock(i)).filter((i) => filter.value === 'all' || (filter.value === 'notes' && i.kind === 'note') || (filter.value === 'activities' && i.kind === 'activity') || (filter.value === 'comments' && i.kind === 'field_comment') || (filter.value === 'changes' && i.kind === 'history')))

function addActivity(type) {
  ui.openCreate({
    entity: 'activity',
    defaults: { type, regarding: { entity: props.entity, uuid: props.record.uuid }, _refs: { regarding: { uuid: props.record.uuid, label: props.record.label, entity: props.entity } } },
    onSaved: () => load(),
  })
}
/** What closed an activity: its outcome or cancellation reason, and the time it took. */
function closingText(a) {
  if (a.status === 'open') return ''
  const reason = a.status === 'cancelled'
    ? a.cancel_reason && meta.dictionaryLabel('activity_cancel_reason', a.cancel_reason)
    : a.outcome && meta.dictionaryLabel('activity_outcome', a.outcome)
  const parts = [reason, a.actual_duration_minutes != null ? t('activity.tookMinutes', { n: a.actual_duration_minutes }) : null].filter(Boolean)
  return parts.join(' · ')
}
function eventText(item) {
  const key = `timeline.events.${item.event}`
  const m = item.meta || {}
  const translated = t(key, { count: m.count ?? '', subject: m.subject ?? '', list: m.marketing_list?.label ?? '', purpose: m.purpose ? meta.dictionaryLabel('consent_purpose', m.purpose) : '',
    number: m.number ?? '', lines: (m.lines || []).join(', '), line: m.line ?? '' })
  return translated === key ? item.event.replace(/_/g, ' ') : translated
}
// A note's files sent from the device since (D-143): the timeline shows them as Core has them.
watch(() => Object.values(pendingUploads.byNote).flat().length, (now, before) => {
  if (now < before) load()
})
defineExpose({ reload: load })
</script>

<template>
  <section class="space-y-4">
    <div class="card p-3">
      <NoteComposer :entity="noteTo?.entity || entity" :uuid="noteTo?.uuid || record.uuid" :reply-to="replyTo" @saved="load()" />
      <!-- an activity is about a record, never about another activity -->
      <!-- a next activity: on a phone the icons alone, after "Create:", so they stay one row -->
      <div v-if="entity !== 'activity'" class="mt-2 flex flex-wrap items-center gap-1 border-t border-default pt-2 sm:gap-2" data-testid="timeline-create">
        <span class="pl-1 text-sm text-muted">{{ $t('timeline.create') }}</span>
        <UiButton v-for="k in CREATE" :key="k.type" size="sm" variant="ghost" :icon="k.icon" :title="$t(k.label)" :aria-label="$t(k.label)" @click="addActivity(k.type)"><span class="hidden sm:inline">{{ $t(k.label) }}</span></UiButton>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-1">
      <button v-for="f in ['all', 'notes', 'activities', 'comments', 'changes']" :key="f" type="button" :class="['rounded-full px-3 py-1 text-xs font-medium', filter === f ? 'bg-brand-600 text-white' : 'text-muted hover:bg-[var(--surface-3)]']" @click="filter = f">{{ $t(`timeline.${f}`) }}</button>
      <span class="flex-1"></span>
      <UiBadge v-if="source === 'replica'" tone="amber" icon="fa-plane">{{ $t('sync.offline') }}</UiBadge>
    </div>
    <!-- what it gathers (D-226): the records around, or the record alone -->
    <div v-if="gathers && source === 'server'" class="flex flex-wrap items-center gap-2 text-xs" data-testid="timeline-scope">
      <select v-if="entity === 'account' && caseOptions.length" v-model="scope" class="input w-auto max-w-full py-1 text-xs" :aria-label="$t('timeline.scope.label')">
        <option value="all">{{ $t('timeline.scope.accountAll') }}</option>
        <option value="own">{{ $t('timeline.scope.accountOwn') }}</option>
        <optgroup :label="$t('timeline.scope.oneCase')">
          <option v-for="c in caseOptions" :key="c.value" :value="c.value">{{ c.label }}</option>
        </optgroup>
      </select>
      <label v-else-if="(entity === 'opportunity' && around?.lead) || entity === 'contact'" class="flex items-center gap-1.5 text-muted">
        <input type="checkbox" :checked="scope === 'all'" @change="scope = $event.target.checked ? 'all' : 'own'" />
        {{ entity === 'opportunity' ? $t('timeline.scope.withLead', { lead: around.lead.label }) : $t('timeline.scope.withConversations') }}
      </label>
    </div>

    <UiSpinner v-if="loading && !items.length" :label="$t('common.loading')" />
    <UiEmpty v-else-if="!visible.length" icon="fa-clock-rotate-left" :title="$t('timeline.empty')" />

    <ol ref="root" class="relative space-y-3 border-l-2 border-default pl-5" data-testid="timeline">
      <li v-for="(item, i) in visible" :key="(item.uuid || item.record?.uuid || '') + i" class="relative rounded-md transition-shadow" :data-item="item.record?.uuid || item.uuid || null">
        <!-- the AI summary covers the items from here down; the newer ones above are not in it yet (D-264) -->
        <div v-if="i === coverageIndex" class="-ml-5 mb-3 flex items-center gap-2 text-xs font-medium text-faint" data-testid="timeline-ai-coverage">
          <span class="h-px flex-1 bg-default"></span>
          <i class="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i><span>{{ $t('ai.coveredHere', { date: formatDateTime(coverage) }) }}</span>
          <span class="h-px flex-1 bg-default"></span>
        </div>
        <!-- the lead became this opportunity: below, the lead's story -->
        <div v-if="i === qualifiedIndex" class="-ml-5 mb-3 flex items-center gap-2 text-xs font-medium text-brand-700" data-testid="timeline-qualified">
          <span class="h-px flex-1 bg-brand-300"></span>
          <i class="fa-solid fa-arrow-up" aria-hidden="true"></i><span v-private>{{ $t('timeline.qualified', { lead: around.lead.label, date: formatDateTime(around.lead.qualified_at) }) }}</span>
          <span class="h-px flex-1 bg-brand-300"></span>
        </div>
        <p v-if="item.origin" class="mb-1 truncate text-2xs text-faint" data-testid="timeline-origin">
          <i :class="['fa-solid mr-1', item.origin.kind === 'participant' ? 'fa-user-check' : ENTITIES[item.origin.entity]?.icon || 'fa-link']" aria-hidden="true"></i>
          <RecordLink v-if="item.origin.uuid" class="hover:underline" :entity="item.origin.entity" :uuid="item.origin.uuid">{{ originText(item.origin) }}</RecordLink>
          <template v-else><span v-private>{{ originText(item.origin) }}</span><template v-if="item.record?.regarding?.label"> — <RecordLink class="hover:underline" :entity="item.record.regarding.entity" :uuid="item.record.regarding.uuid">{{ item.record.regarding.label }}</RecordLink></template></template>
        </p>
        <span
          class="absolute -left-[1.95rem] top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[var(--surface-2)] text-3xs text-white"
          :class="item.kind === 'note' ? 'bg-yellow-500' : item.kind === 'activity' ? 'bg-rose-500' : item.kind === 'mention' ? 'bg-violet-500' : item.kind === 'field_comment' ? 'bg-sky-500' : 'bg-slate-400'"
        >
          <i :class="['fa-solid', item.kind === 'note' ? 'fa-note-sticky' : item.kind === 'activity' ? activityTypeIcon(item.record.type) : item.kind === 'mention' ? 'fa-at' : item.kind === 'field_comment' ? 'fa-comment' : 'fa-pen']" aria-hidden="true"></i>
        </span>

        <article v-if="item.kind === 'note'" class="card p-3" data-kind="note">
          <header class="mb-1 flex items-center gap-2 text-xs text-muted">
            <UiAvatar :label="item.record.author?.label || item.record.owner?.label || ''" size="xs" />
            <span class="font-medium text-[var(--text)]">{{ item.record.author?.label || item.record.owner?.label }}</span>
            <span :title="formatDateTime(item.at)">{{ formatRelative(item.at) }}</span>
            <i v-if="item.record.is_private" class="fa-solid fa-lock" :title="$t('record.private')" aria-hidden="true"></i>
            <i v-if="item.record.is_pinned" class="fa-solid fa-thumbtack text-amber-500" aria-hidden="true"></i>
            <i v-if="item.record._pending" class="fa-solid fa-cloud-arrow-up text-blue-500" :title="$t('record.syncPending')" aria-hidden="true"></i>
            <!-- a note on one of this record's activities: which one -->
            <span v-if="onOther(item.record)" class="truncate" data-testid="note-on">
              · {{ $t('timeline.noteOn') }} <RecordLink class="link" :entity="item.record.noteable.entity" :uuid="item.record.noteable.uuid"><i :class="['fa-solid', ENTITIES[item.record.noteable.entity]?.icon || 'fa-link']" aria-hidden="true"></i> {{ item.record.noteable.label }}</RecordLink>
            </span>
            <span class="flex-1"></span>
            <RecordLink class="link" entity="note" :uuid="item.record.uuid">{{ $t('common.open') }}</RecordLink>
            <UiMenu v-if="noteActions(item.record).length" :items="noteActions(item.record)" :tip="$t('notes.actions.more')" button-class="!p-1 text-xs" data-testid="note-actions" />
          </header>
          <RichText class="text-sm" :doc="item.record.body" :fallback="item.record.body_text || ''" />
          <NoteAttachments :note="item.record" />
        </article>

        <article v-else-if="item.kind === 'activity'" class="card flex items-start gap-3 p-3" data-kind="activity">
          <div class="min-w-0 flex-1">
            <PriorityIcon :priority="item.record.priority" class="mr-1" />
            <RecordLink class="font-medium hover:underline" entity="activity" :uuid="item.record.uuid">{{ item.record.subject }}</RecordLink>
            <FollowUpBadge class="ml-1.5" :activity="item.record" />
            <div class="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted">
              <span>{{ enumLabel('activity', 'type', item.record.type) }}</span>
              <UiBadge :tone="item.record.overdue ? 'red' : STATUS_TONES[item.record.status]">{{ statusLabel('activity', item.record.status) }}</UiBadge>
              <span :title="formatDateTime(item.at)">{{ formatDateTime(item.record.start_at || item.record.due_at || item.at) }}</span>
              <span v-if="item.record.owner">· {{ item.record.owner.label }}</span>
              <span v-if="item.record.sequence">· {{ $t('work.fromSequence', { name: item.record.sequence.label }) }}</span>
            </div>
            <!-- what it is about, as written in it: its start, the whole on a click -->
            <ClampedRichText v-if="hasText(item.record.description)" class="mt-1 text-sm" :doc="item.record.description" :fallback="item.record.description_text || ''" data-testid="activity-description" />
            <p v-if="closingText(item.record)" v-private class="mt-1 text-xs text-muted" data-testid="activity-closing">{{ closingText(item.record) }}</p>
            <!-- its notes: what closing it said, a call's summary (D-294) -->
            <ul v-if="notesOf[item.record.uuid]" class="mt-2 space-y-2 border-l-2 border-yellow-400 pl-3" data-testid="activity-notes">
              <li v-for="n in notesOf[item.record.uuid]" :key="n.record.uuid" class="rounded-md transition-shadow" :data-item="n.record.uuid" data-kind="note">
                <p class="mb-0.5 flex items-center gap-2 text-xs text-muted">
                  <span class="font-medium text-[var(--text)]">{{ n.record.author?.label || n.record.owner?.label }}</span>
                  <span :title="formatDateTime(n.at)">{{ formatRelative(n.at) }}</span>
                  <i v-if="n.record.is_private" class="fa-solid fa-lock" :title="$t('record.private')" aria-hidden="true"></i>
                  <i v-if="n.record._pending" class="fa-solid fa-cloud-arrow-up text-blue-500" :title="$t('record.syncPending')" aria-hidden="true"></i>
                  <span class="flex-1"></span>
                  <RecordLink class="link" entity="note" :uuid="n.record.uuid">{{ $t('common.open') }}</RecordLink>
                  <UiMenu v-if="noteActions(n.record).length" :items="noteActions(n.record)" :tip="$t('notes.actions.more')" button-class="!p-1 text-xs" data-testid="note-actions" />
                </p>
                <RichText class="text-sm" :doc="n.record.body" :fallback="n.record.body_text || ''" />
                <NoteAttachments :note="n.record" />
              </li>
            </ul>
          </div>
          <!-- whom the call is with and the number, as on the work list (D-249) -->
          <ActivityActions :activity="item.record" :person="item.record.person || null" :phone="item.record.person?.phone || null" compact @changed="load()" />
        </article>

        <article v-else-if="item.kind === 'mention'" class="card p-3 text-sm" data-kind="mention">
          <p class="text-xs text-muted">{{ $t('timeline.events.mentioned') }} · {{ formatRelative(item.at) }}</p>
          <RecordLink v-if="item.source && ENTITIES[item.source.entity]" class="link" :entity="item.source.entity" :uuid="item.source.uuid">{{ item.source.label }}</RecordLink>
          <p v-private class="mt-1 text-muted">{{ item.text }}</p>
        </article>

        <article v-else-if="item.kind === 'field_comment'" class="card p-3 text-sm" data-kind="field_comment">
          <p class="flex flex-wrap items-center gap-x-1.5 text-xs text-muted">
            <span class="font-medium text-[var(--text)]">{{ item.actor?.label }}</span>
            <span>{{ $t('timeline.commentedOn', { field: fieldLabel(entity, item.field.startsWith('cf:') ? `cf.${item.field.slice(3)}` : item.field) }) }}</span>
            <span :title="formatDateTime(item.at)">· {{ formatRelative(item.at) }}</span>
            <i v-if="item.is_private" class="fa-solid fa-lock" :title="$t('fieldComments.private')" aria-hidden="true"></i>
            <UiBadge v-if="item.resolved" tone="green" icon="fa-check">{{ $t('timeline.resolved') }}</UiBadge>
          </p>
          <p v-private :class="['mt-1 whitespace-pre-line', item.resolved ? 'text-muted line-through decoration-1' : '']">{{ item.text }}</p>
        </article>

        <article v-else class="px-1 text-sm" data-kind="history">
          <p class="flex flex-wrap items-center gap-x-1.5 text-muted">
            <span class="font-medium text-[var(--text)]">{{ item.actor?.label || $t('record.system') }}</span>
            <span>{{ eventText(item) }}</span>
            <span class="text-xs" :title="formatDateTime(item.at)">· {{ formatRelative(item.occurred_at || item.at) }}</span>
            <UiBadge v-if="item.source === 'device'" tone="blue" icon="fa-plane">{{ $t('record.viaDevice') }}</UiBadge>
            <UiBadge v-if="item.source === 'hub'" tone="violet" icon="fa-diagram-project">{{ item.meta?.producer || 'hub' }}</UiBadge>
          </p>
          <ChangeList v-if="item.event !== 'created'" :entity="item.origin?.entity || entity" :from="item.from" :to="item.to" class="mt-1" />
          <p v-if="item.meta?.label && item.event === 'external_event'" v-private class="mt-1">{{ item.meta.label }}</p>
          <p v-if="item.note" v-private class="mt-1 whitespace-pre-line rounded-lg bg-[var(--surface-3)] px-2 py-1 text-xs">{{ item.note }}</p>
        </article>
      </li>
    </ol>
    <UiButton v-if="nextBefore && source === 'server'" block :loading="loading" @click="load(true)">{{ $t('timeline.showMore') }}</UiButton>
    <NoteMoveDialog v-if="moving" :note="moving" @close="moving = null" @chosen="moved" />
    <NoteToActivityDialog v-if="converting" :note="converting" @close="converting = null" @done="converting = null; load()" />
  </section>
</template>
